import { lazy, Suspense } from "react";
import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  isAuthorStory,
  type CoachFeedback,
  type InventoryItemView,
  type LearningView,
  type PlayMessageView,
  type PlaySessionStatus,
  type PlaySessionView,
  type PlayStreamEvent,
  type PlayToolEvent,
  type PlayerChapterView,
  type PlayerSceneView,
  type PlaytestDebug,
  type SessionPoints,
  type VocabCardView,
} from "@storyai/shared";
import { Badge, Crumbs, EmptyState, ErrorPanel, Loading } from "../components/ui";
import { ApiError, sessionsApi } from "../lib/api";
import {
  keys,
  useAnswerCard,
  useInvalidateSessionLists,
  useModels,
  useRenameSession,
  useSession,
  useStory,
  useUpdateSession,
  useUpdateStory,
} from "../lib/queries";
import { KebabMenu, type MenuItem } from "../components/KebabMenu";
import { Glossed } from "../components/learning/Glossed";
import { VocabCard } from "../components/learning/VocabCard";
import { CoachBubble } from "../components/learning/CoachBubble";
import { mergePendingCards, pointsDelta, ruleChips, totalPoints, transitionText } from "../lib/learningState";

// ---- Local state ------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "coach";
  content: string;
  tools: PlayToolEvent[];
  pending: boolean;
  createdAt: string;
  /** role "coach": the feedback to render. */
  coach?: CoachFeedback;
}

interface Banner {
  kind: "chapter" | "fail" | "end";
  text: string;
}

interface Toast {
  id: number;
  text: string;
}

interface PlayState {
  status: PlaySessionStatus;
  chapter: PlayerChapterView | null;
  scene: PlayerSceneView | null;
  inventory: InventoryItemView[];
  messages: ChatMessage[];
  debug?: PlaytestDebug;
  learning: LearningView | null;
  toasts: Toast[];
  banner: Banner | null;
  streaming: boolean;
  error: string | null;
  /** Bumps on chapter change so the chapter card re-animates. */
  chapterKey: number;
  /** Set when the coach blocked the last message; the page restores it to the input. */
  blockedText: string | null;
  /**
   * A chapter change the server has already made but the player hasn't
   * acknowledged. Held so the GM's final reply stays readable; applied on
   * Next. (A reload while pending simply shows the new chapter, since the
   * session itself has moved on.)
   */
  pendingTransition: ChapterEvent | null;
  /** A non-narration notice shown above the input until the next send. */
  notice: string | null;
}

type ChapterEvent = Extract<PlayStreamEvent, { type: "chapter" }>;

type Action =
  | { type: "init"; view: PlaySessionView }
  | { type: "applyTransition" }
  | { type: "send"; user: ChatMessage; pendingId: string }
  | { type: "event"; event: PlayStreamEvent; pendingId: string; userId: string }
  | { type: "finish"; pendingId: string }
  | { type: "fail"; pendingId: string; error: string }
  | { type: "clearError" }
  | { type: "cards"; pendingCards: VocabCardView[]; points: SessionPoints }
  | { type: "toastExpire"; id: number }
  | { type: "consumeBlocked" };

let toastSeq = 0;

function fromView(view: PlaySessionView, prev?: PlayState): PlayState {
  const finished = view.session.status === "finished";
  return {
    status: view.session.status,
    chapter: view.chapter,
    scene: view.scene,
    inventory: view.inventory,
    messages: view.messages.flatMap(toChatMessages),
    debug: view.debug,
    learning: view.learning ?? null,
    toasts: prev?.toasts ?? [],
    banner: finished ? { kind: "end", text: "The End" } : null,
    streaming: false,
    error: null,
    chapterKey: prev?.chapterKey ?? 0,
    blockedText: null,
    pendingTransition: null,
    notice: null,
  };
}

/** A persisted user message with coach feedback renders as the user bubble followed by the coach bubble. */
function toChatMessages(m: PlayMessageView): ChatMessage[] {
  const base: ChatMessage = { id: m.id, role: m.role, content: m.content, tools: m.tools, pending: false, createdAt: m.createdAt };
  if (m.role === "user" && m.coach) {
    return [base, { id: `${m.id}-coach`, role: "coach", content: "", tools: [], pending: false, createdAt: m.createdAt, coach: m.coach }];
  }
  return [base];
}

function updatePending(messages: ChatMessage[], pendingId: string, fn: (m: ChatMessage) => ChatMessage): ChatMessage[] {
  return messages.map((m) => (m.id === pendingId ? fn(m) : m));
}

function withToasts(state: PlayState, before: SessionPoints, after: SessionPoints): Toast[] {
  const label = { reading: "Reading", vocabulary: "Vocabulary", writing: "Writing" } as const;
  const fresh = pointsDelta(before, after).map((d) => ({ id: ++toastSeq, text: `+${d.amount} ${label[d.kind]}` }));
  return [...state.toasts, ...fresh];
}

function reducer(state: PlayState, action: Action): PlayState {
  switch (action.type) {
    case "init":
      return fromView(action.view, state);

    case "send": {
      const pending: ChatMessage = {
        id: action.pendingId,
        role: "assistant",
        content: "",
        tools: [],
        pending: true,
        createdAt: new Date().toISOString(),
      };
      return {
        ...state,
        messages: [...state.messages, action.user, pending],
        streaming: true,
        error: null,
        blockedText: null,
        notice: null,
        banner: state.banner?.kind === "end" ? state.banner : null,
      };
    }

    case "event": {
      const e = action.event;
      const pid = action.pendingId;
      switch (e.type) {
        case "token":
          return { ...state, messages: updatePending(state.messages, pid, (m) => ({ ...m, content: m.content + e.text })) };
        case "tool":
          return { ...state, messages: updatePending(state.messages, pid, (m) => ({ ...m, tools: [...m.tools, e.tool] })) };
        case "state":
          return { ...state, inventory: e.inventory, scene: e.scene };
        case "chapter":
          // Don't move yet: let the player read the reply, then press Next.
          return { ...state, pendingTransition: e };
        case "notice":
          return { ...state, notice: e.text };
        case "end":
          return { ...state, status: "finished", banner: { kind: "end", text: "The End" } };
        case "debug":
          return { ...state, debug: e.debug };
        case "coach": {
          const coachMsg: ChatMessage = {
            id: `${pid}-coach`,
            role: "coach",
            content: "",
            tools: [],
            pending: false,
            createdAt: new Date().toISOString(),
            coach: e.feedback,
          };
          const before = state.learning?.points ?? { reading: 0, vocabulary: 0, writing: 0 };
          const after = {
            reading: before.reading + e.feedback.points.reading,
            vocabulary: before.vocabulary + e.feedback.points.vocabulary,
            writing: before.writing + e.feedback.points.writing,
          };
          const learning = state.learning ? { ...state.learning, points: after } : state.learning;
          if (!e.feedback.ok) {
            // Blocked: drop the user bubble and the pending GM reply; keep the coach note.
            const user = state.messages.find((m) => m.id === action.userId);
            return {
              ...state,
              learning,
              blockedText: user?.content ?? null,
              messages: [...state.messages.filter((m) => m.id !== action.userId && m.id !== pid), coachMsg],
            };
          }
          // Insert the coach bubble between the user message and the pending GM reply.
          const idx = state.messages.findIndex((m) => m.id === pid);
          const messages = [...state.messages];
          messages.splice(idx < 0 ? messages.length : idx, 0, coachMsg);
          return { ...state, learning, messages, toasts: withToasts(state, before, after) };
        }
        case "vocab": {
          if (!state.learning) return state;
          return { ...state, learning: { ...state.learning, pendingCards: mergePendingCards(state.learning.pendingCards, e.cards) } };
        }
        case "learning": {
          if (!state.learning) return state;
          return {
            ...state,
            toasts: withToasts(state, state.learning.points, e.points),
            learning: { ...state.learning, targetWords: e.targetWords, points: e.points },
          };
        }
        case "done": {
          const exists = state.messages.some((m) => m.id === pid);
          if (!exists) return state; // chat was cleared by a chapter change, or blocked
          const persisted = toChatMessages(e.message);
          return { ...state, messages: state.messages.flatMap((m) => (m.id === pid ? persisted : [m])) };
        }
        case "error":
          return { ...state, messages: state.messages.filter((m) => m.id !== pid), error: e.error };
        default:
          return state;
      }
    }

    case "finish":
      return {
        ...state,
        streaming: false,
        messages: state.messages
          .filter((m) => !(m.id === action.pendingId && m.pending && !m.content && m.tools.length === 0))
          .map((m) => (m.id === action.pendingId ? { ...m, pending: false } : m)),
      };

    case "fail":
      return {
        ...state,
        streaming: false,
        error: action.error,
        messages: state.messages.filter((m) => m.id !== action.pendingId),
      };

    case "clearError":
      return { ...state, error: null };

    case "cards": {
      if (!state.learning) return state;
      return {
        ...state,
        toasts: withToasts(state, state.learning.points, action.points),
        learning: { ...state.learning, pendingCards: action.pendingCards, points: action.points },
      };
    }

    case "toastExpire":
      return { ...state, toasts: state.toasts.filter((t) => t.id !== action.id) };

    case "consumeBlocked":
      return { ...state, blockedText: null };

    case "applyTransition": {
      const e = state.pendingTransition;
      if (!e) return state;
      return {
        ...state,
        pendingTransition: null,
        notice: null,
        chapter: e.chapter,
        scene: e.scene,
        inventory: e.inventory,
        messages: [],
        chapterKey: state.chapterKey + 1,
        learning: state.learning ? { ...state.learning, pendingCards: [], targetWords: [] } : null,
        banner:
          e.reason === "fail"
            ? { kind: "fail", text: e.message ?? "You failed. The story resets." }
            : { kind: "chapter", text: "Chapter changed" },
      };
    }
  }
}

// ---- Page -------------------------------------------------------------------

export function PlaySessionPage() {
  const { storyId = "", sessionId = "" } = useParams();
  const q = useSession(storyId, sessionId);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;

  const view = q.data.session;

  if (!view.storyAvailable) {
    return (
      <div className="page">
        <Crumbs parts={[{ to: "/playing", label: "Playing" }, { label: view.session.name }]} />
        <EmptyState title="This story has been unpublished by the author.">
          Your session is kept, and will work again if the author republishes.{" "}
          <Link to="/playing">Back to Playing</Link>
        </EmptyState>
      </div>
    );
  }

  return <PlayView key={sessionId} storyId={storyId} sessionId={sessionId} view={view} />;
}

const MapOverlay = lazy(() => import("../components/map/MapOverlay").then((m) => ({ default: m.MapOverlay })));

function PlayView({
  storyId,
  sessionId,
  view,
}: {
  storyId: string;
  sessionId: string;
  view: PlaySessionView;
}) {
  const [state, dispatch] = useReducer(reducer, view, (v) => fromView(v));
  const [input, setInput] = useState("");
  const [mapExpanded, setMapExpanded] = useState(false);
  const closeMapOverlay = () => setMapExpanded(false);
  const [mapTick, setMapTick] = useState(0);
  const invalidateLists = useInvalidateSessionLists();
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Re-seed if the query data changes underneath us while idle (e.g. a
  // remount with fresh data, or the post-chapter refetch below).
  useEffect(() => {
    if (!state.streaming) dispatch({ type: "init", view });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // The coach bounced the message: put the text back so it can be fixed.
  useEffect(() => {
    if (state.blockedText !== null && !state.streaming) {
      setInput(state.blockedText);
      dispatch({ type: "consumeBlocked" });
      textareaRef.current?.focus();
    }
  }, [state.blockedText, state.streaming]);

  // Toasts fade after 2s.
  useEffect(() => {
    if (state.toasts.length === 0) return;
    const t = state.toasts[state.toasts.length - 1]!;
    const h = window.setTimeout(() => dispatch({ type: "toastExpire", id: t.id }), 2000);
    return () => window.clearTimeout(h);
  }, [state.toasts]);

  const last = state.messages[state.messages.length - 1];
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [state.messages.length, last?.content, last?.tools.length, state.banner, state.learning?.pendingCards.length]);

  const learning = state.learning;
  const cardsPending = (learning?.pendingCards.length ?? 0) > 0;
  const transitionPending = state.pendingTransition !== null;
  const canSend = state.status === "active" && !state.streaming && !cardsPending && !transitionPending;

  const send = useCallback(async () => {
    const content = input.trim();
    if (!content || !canSend) return;
    const stamp = Date.now();
    const userId = `local-user-${stamp}`;
    const pendingId = `local-gm-${stamp}`;
    dispatch({
      type: "send",
      pendingId,
      user: { id: userId, role: "user", content, tools: [], pending: false, createdAt: new Date().toISOString() },
    });
    setInput("");

    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await sessionsApi.sendMessage(
        storyId,
        sessionId,
        content,
        (event) => {
          dispatch({ type: "event", event, pendingId, userId });
          if (event.type === "chapter" || event.type === "done") setMapTick((t) => t + 1);
        },
        ac.signal,
      );
      dispatch({ type: "finish", pendingId });
    } catch (err) {
      if (ac.signal.aborted) return;
      const msg =
        err instanceof ApiError
          ? err.message
          : "Lost the connection to the storyteller. Your message was kept; try again.";
      dispatch({ type: "fail", pendingId, error: msg });
    } finally {
      abortRef.current = null;
      invalidateLists(storyId);
      textareaRef.current?.focus();
    }
  }, [input, canSend, storyId, sessionId, invalidateLists, qc]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  // Next: apply the held chapter change, then refetch so the new chapter's
  // passage cards, glossary and target words load.
  const onNext = useCallback(() => {
    dispatch({ type: "applyTransition" });
    void qc.invalidateQueries({ queryKey: keys.session(storyId, sessionId) });
    textareaRef.current?.focus();
  }, [qc, storyId, sessionId]);

  // ---- vocabulary cards ----
  const answer = useAnswerCard(storyId, sessionId);
  const onAnswer = useCallback(
    async (vocabWordId: string, choice: 0 | 1 | 2) => {
      try {
        const res = await answer.mutateAsync({ vocabWordId, choice });
        dispatch({ type: "cards", pendingCards: res.pendingCards, points: res.sessionPoints });
        return { correct: res.correct, points: res.points };
      } catch {
        return undefined;
      }
    },
    [answer],
  );

  const isPlaytest = view.session.isPlaytest;

  // ---- settings menu: model choice (applies next turn) + rename (+ coach model for authors) ----
  const models = useModels("text");
  const updateSession = useUpdateSession(storyId, sessionId);
  const storyQ = useStory(storyId);
  const updateStory = useUpdateStory(storyId);
  const [renameSignal, setRenameSignal] = useState(0);
  const sessionModelId = view.session.modelId ?? null;
  const modelList = models.data?.models ?? [];
  const currentModel = modelList.find((m) => (sessionModelId ? m.id === sessionModelId : m.isDefault));
  const authorStory = storyQ.data && storyQ.data.isAuthor && isAuthorStory(storyQ.data.story) ? storyQ.data.story : null;
  const coachModelId = authorStory?.coachModelId ?? null;
  const currentCoach = modelList.find((m) => (coachModelId ? m.id === coachModelId : m.isDefault));
  const menuItems: MenuItem[] = [
    {
      kind: "submenu",
      label: "Model",
      valueLabel: currentModel?.name ?? "Default",
      children: modelList.map((m) => ({
        kind: "radio" as const,
        label: m.name,
        description: m.description,
        checked: sessionModelId ? m.id === sessionModelId : m.isDefault,
        disabled: updateSession.isPending,
        onSelect: () => updateSession.mutate({ modelId: m.id }),
      })),
    },
    ...(isPlaytest && authorStory
      ? [
          {
            kind: "submenu" as const,
            label: "Coach model",
            valueLabel: currentCoach?.name ?? "Default",
            children: modelList.map((m) => ({
              kind: "radio" as const,
              label: m.name,
              description: m.description,
              checked: coachModelId ? m.id === coachModelId : m.isDefault,
              disabled: updateStory.isPending,
              onSelect: () => updateStory.mutate({ coachModelId: m.id }),
            })),
          },
        ]
      : []),
    { kind: "action", label: "Rename session", onSelect: () => setRenameSignal((n) => n + 1) },
  ];

  const glossary = learning?.glossary ?? [];
  const passageCards = learning?.pendingCards.filter((c) => c.kind === "passage") ?? [];
  const wovenCards = learning?.pendingCards.filter((c) => c.kind === "woven") ?? [];
  const knownIds = new Set(state.messages.map((m) => m.id));
  const orphanWoven = wovenCards.filter((c) => !c.messageId || !knownIds.has(c.messageId));

  return (
    <div className="page play-page">
      <div className="play-topbar">
        <Crumbs
          parts={[
            { to: "/playing", label: "Playing" },
            {
              to: `/stories/${storyId}`,
              label: view.session.versionTitle
                ? `${view.session.storyTitle} · ${view.session.versionTitle}`
                : view.session.storyTitle,
            },
          ]}
        />
        <div className="play-topbar-right">
          <SessionName storyId={storyId} sessionId={sessionId} initial={view.session.name} editSignal={renameSignal} />
          {isPlaytest && <Badge tone="accent">Playtest</Badge>}
          <KebabMenu items={menuItems} label="Session options" />
        </div>
      </div>

      <div className="play-layout">
        <div className="play-main">
          {state.chapter ? (
            <ChapterCard
              key={state.chapterKey}
              chapter={state.chapter}
              animate={state.chapterKey > 0}
              glossary={learning ? glossary : null}
            />
          ) : (
            <EmptyState title="This story has no starting chapter yet.">
              The author hasn't added a chapter to start from.
            </EmptyState>
          )}

          {passageCards.length > 0 && (
            <div className="vocab-cards" aria-label="Vocabulary">
              {passageCards.map((c) => (
                <VocabCard key={c.vocabWordId} card={c} busy={answer.isPending} onAnswer={(choice) => onAnswer(c.vocabWordId, choice)} />
              ))}
            </div>
          )}

          {state.banner && <BannerView banner={state.banner} />}

          <section className="chat" aria-live="polite">
            {state.messages.length === 0 && !state.banner && (
              <p className="muted small chat-hint">
                {cardsPending ? "Answer the vocabulary cards, then type what you do." : "Read the chapter above, then type what you do."}
              </p>
            )}
            {state.messages.map((m) => (
              <MessageView
                key={m.id}
                message={m}
                isPlaytest={isPlaytest}
                glossary={learning ? glossary : null}
                cards={wovenCards.filter((c) => c.messageId === m.id)}
                busy={answer.isPending}
                onAnswer={onAnswer}
              />
            ))}
            {orphanWoven.length > 0 && (
              <div className="vocab-cards">
                {orphanWoven.map((c) => (
                  <VocabCard key={c.vocabWordId} card={c} busy={answer.isPending} onAnswer={(choice) => onAnswer(c.vocabWordId, choice)} />
                ))}
              </div>
            )}
            {state.pendingTransition && (
              <div className="transition-card" role="status">
                <p>{transitionText(state.pendingTransition)}</p>
                <button type="button" className="btn btn-primary" onClick={onNext} disabled={state.streaming}>
                  Next
                </button>
              </div>
            )}
            <div ref={endRef} />
          </section>

          {state.toasts.length > 0 && (
            <div className="points-toasts" aria-live="polite">
              {state.toasts.map((t) => (
                <span key={t.id} className="points-toast">
                  {t.text}
                </span>
              ))}
            </div>
          )}

          {state.notice && (
            <div className="notice-banner" role="status">
              {state.notice}
            </div>
          )}

          <form className="chat-input" onSubmit={onSubmit}>
            <textarea
              ref={textareaRef}
              rows={2}
              className={cardsPending || transitionPending ? "locked" : undefined}
              placeholder={
                state.status === "finished"
                  ? "The story has ended."
                  : transitionPending
                    ? "Click Next to continue"
                    : cardsPending
                      ? "Answer the vocabulary cards to continue"
                      : state.streaming
                        ? "The storyteller is writing…"
                        : "What do you do?"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={!canSend}
              aria-label="Your action"
            />
            <button type="submit" className="btn btn-primary" disabled={!canSend || !input.trim()}>
              {state.streaming ? "…" : "Send"}
            </button>
          </form>
          {state.error && (
            <div className="error" role="alert">
              {state.error}{" "}
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => dispatch({ type: "clearError" })}>
                Dismiss
              </button>
            </div>
          )}
        </div>

        <aside className="play-aside">
          {state.scene && (
            <div className="aside-block">
              <div className="aside-label">Location</div>
              <div>{state.scene.name}</div>
            </div>
          )}
          <div className="aside-block">
            <div className="aside-label">Inventory</div>
            {state.inventory.length === 0 ? (
              <div className="muted small">Nothing yet.</div>
            ) : (
              <ul className="inventory-list">
                {state.inventory.map((it) => (
                  <li key={it.key}>
                    {it.imageUrl && <img className="inventory-icon" src={it.imageUrl} alt="" />}
                    {it.description ? (
                      <details>
                        <summary title={it.description}>{it.name}</summary>
                        <div className="muted small">{it.description}</div>
                      </details>
                    ) : (
                      <span>{it.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {learning && <LearningAside learning={learning} />}
          <div className="aside-block">
            <button type="button" className="btn btn-sm" onClick={() => setMapExpanded(true)}>
              Map
            </button>
          </div>
          {mapExpanded && (
            <Suspense fallback={null}>
              <MapOverlay storyId={storyId} sessionId={sessionId} tick={mapTick} onClose={closeMapOverlay} />
            </Suspense>
          )}
          {isPlaytest && state.debug && <DebugPanel debug={state.debug} />}
        </aside>
      </div>
    </div>
  );
}

// ---- Pieces -----------------------------------------------------------------

function Paragraphs({ text }: { text: string }) {
  const parts = text.split(/\n{2,}/).filter((p) => p.trim().length > 0);
  if (parts.length === 0) return null;
  return (
    <>
      {parts.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Prose({ text, glossary }: { text: string; glossary: LearningView["glossary"] | null }) {
  if (glossary && glossary.length > 0) return <Glossed text={text} glossary={glossary} />;
  return <Paragraphs text={text} />;
}

function ChapterCard({
  chapter,
  animate,
  glossary,
}: {
  chapter: PlayerChapterView;
  animate: boolean;
  glossary: LearningView["glossary"] | null;
}) {
  return (
    <article className={`chapter-card ${animate ? "chapter-card-enter" : ""}`}>
      {chapter.imageUrl && <img className="chapter-card-img" src={chapter.imageUrl} alt="" />}
      <div className="chapter-card-body">
        <div className="chapter-card-kicker muted small">
          {chapter.isEnding ? "Ending" : "Chapter"}
        </div>
        <h1 className="chapter-card-title">{chapter.title}</h1>
        <div className="chapter-desc">
          {chapter.description ? <Prose text={chapter.description} glossary={glossary} /> : <p className="muted">…</p>}
        </div>
      </div>
    </article>
  );
}

function BannerView({ banner }: { banner: Banner }) {
  return (
    <div className={`banner banner-${banner.kind}`} role="status">
      {banner.text}
    </div>
  );
}

function MessageView({
  message,
  isPlaytest,
  glossary,
  cards,
  busy,
  onAnswer,
}: {
  message: ChatMessage;
  isPlaytest: boolean;
  glossary: LearningView["glossary"] | null;
  cards: VocabCardView[];
  busy: boolean;
  onAnswer: (vocabWordId: string, choice: 0 | 1 | 2) => Promise<{ correct: boolean; points: number } | undefined>;
}) {
  if (message.role === "coach" && message.coach) {
    return <CoachBubble feedback={message.coach} />;
  }
  if (message.role === "user") {
    return (
      <div className="msg msg-user">
        <div className="msg-body">
          <Paragraphs text={message.content} />
        </div>
      </div>
    );
  }
  const chips = message.tools.filter((t) => t.label || (isPlaytest && !t.ok));
  const empty = !message.content && chips.length === 0;
  // A persisted reply with no text (e.g. the model led with a transition
  // tool) renders its chips alone rather than an empty prose box; a fully
  // empty, settled message renders nothing.
  if (!message.pending && !message.content && chips.length === 0) return null;
  const showBody = message.pending || !!message.content;
  return (
    <>
      <div className="msg msg-gm">
        {showBody && (
          <div className="msg-body">
            {empty && message.pending ? (
              <span className="typing" aria-label="The storyteller is writing">
                <span />
                <span />
                <span />
              </span>
            ) : (
              <Prose text={message.content} glossary={glossary} />
            )}
            {message.pending && message.content && <span className="cursor" aria-hidden="true" />}
          </div>
        )}
        {chips.length > 0 && (
          <div className="tool-chips">
            {chips.map((t, i) => (
              <span key={i} className={`chip ${t.ok ? "chip-ok" : "chip-error"}${t.imageUrl ? " chip-with-icon" : ""}`} title={t.name}>
                {t.imageUrl && <img className="chip-icon" src={t.imageUrl} alt="" draggable={false} />}
                {t.label || t.name}
                {isPlaytest && !t.ok && t.error && <span className="chip-detail"> — {t.error}</span>}
              </span>
            ))}
          </div>
        )}
      </div>
      {cards.length > 0 && (
        <div className="vocab-cards">
          {cards.map((c) => (
            <VocabCard key={c.vocabWordId} card={c} busy={busy} onAnswer={(choice) => onAnswer(c.vocabWordId, choice)} />
          ))}
        </div>
      )}
    </>
  );
}

function LearningAside({ learning }: { learning: LearningView }) {
  const chips = ruleChips(learning.rules);
  return (
    <>
      {learning.targetWords.length > 0 && (
        <div className="aside-block">
          <div className="aside-label">Words to use</div>
          <ul className="target-list">
            {learning.targetWords.map((t) => (
              <li key={t.targetWordId}>
                <span className={`target-mark${t.used ? " done" : ""}`} aria-hidden="true">
                  {t.used ? "✓" : "○"}
                </span>
                <span>{t.word}</span>
                {t.used && t.correct === true && <span className="target-note">used well</span>}
                {t.used && t.correct === false && <span className="target-note retry">try again</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {chips.length > 0 && (
        <div className="aside-block">
          <div className="aside-label">Writing rules</div>
          <div className="rule-chips">
            {chips.map((c) => (
              <span key={c.key} className={`rule-chip${c.required ? " required" : ""}`} title={c.required ? "Required" : "Feedback"}>
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="aside-block">
        <div className="aside-label">Points · {totalPoints(learning.points)}</div>
        <div className="points-grid">
          {(["reading", "vocabulary", "writing"] as const).map((k) => (
            <div key={k} className="points-tile">
              <div className="n">{learning.points[k]}</div>
              <div className="k">{k === "vocabulary" ? "Vocab" : k}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function SessionName({
  storyId,
  sessionId,
  initial,
  editSignal,
}: {
  storyId: string;
  sessionId: string;
  initial: string;
  /** Increment to start editing from outside (the ⋮ menu's "Rename session"). */
  editSignal?: number;
}) {
  const [name, setName] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const rename = useRenameSession(storyId, sessionId);

  useEffect(() => {
    setName(initial);
    setDraft(initial);
  }, [initial]);

  useEffect(() => {
    if (editSignal) setEditing(true);
  }, [editSignal]);

  async function commit() {
    const next = draft.trim();
    setEditing(false);
    if (!next || next === name) {
      setDraft(name);
      return;
    }
    try {
      const res = await rename.mutateAsync(next);
      setName(res.session.name);
      setDraft(res.session.name);
    } catch {
      setDraft(name);
    }
  }

  if (editing) {
    return (
      <form
        className="session-name-edit"
        onSubmit={(e) => {
          e.preventDefault();
          void commit();
        }}
      >
        <input
          autoFocus
          value={draft}
          maxLength={80}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setDraft(name);
              setEditing(false);
            }
          }}
          aria-label="Session name"
        />
      </form>
    );
  }
  return (
    <button
      type="button"
      className="session-name"
      onClick={() => setEditing(true)}
      title="Rename session"
      disabled={rename.isPending}
    >
      {name}
      <span className="muted small session-name-hint"> ✎</span>
    </button>
  );
}

function DebugPanel({ debug }: { debug: PlaytestDebug }) {
  const flags = Object.entries(debug.flags);
  const summaries = Object.entries(debug.summaries);
  return (
    <details className="aside-block debug-panel">
      <summary className="aside-label">Debug (playtest)</summary>
      <div className="debug-section">
        <div className="aside-sublabel">Flags</div>
        {flags.length === 0 ? (
          <div className="muted small">none</div>
        ) : (
          <ul className="plain-list mono small">
            {flags.map(([k, v]) => (
              <li key={k}>
                {k} = {String(v)}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="debug-section">
        <div className="aside-sublabel">Visited chapters</div>
        <div className="small">{debug.visitedChapterIds.length}</div>
      </div>
      <div className="debug-section">
        <div className="aside-sublabel">Summaries</div>
        {summaries.length === 0 ? (
          <div className="muted small">none yet</div>
        ) : (
          <ul className="plain-list small">
            {summaries.map(([id, s]) => (
              <li key={id}>
                <span className="mono muted">{id.slice(0, 8)}</span>: {s}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="debug-section">
        <div className="aside-sublabel">System prompt</div>
        <pre className="debug-pre">{debug.systemPrompt}</pre>
      </div>
    </details>
  );
}
